import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SEO from "../components/SEO";

// ============================================================
// CHARACTER DATA — edit names, bios, photos, and window
// coordinates here. Coordinates are percentages of the
// building image's width and height.
// ============================================================
const characters = [
  {
    id: "character-1",
    name: "CHARACTER NAME",
    bio: "First sentence of the bio goes here. Second sentence goes here.",
    photo: "/images/cast/character-1.jpg",
    window: { left: 11.3, top: 19.8, width: 9.0, height: 11.1 }
  },
  {
    id: "character-2",
    name: "CHARACTER NAME",
    bio: "First sentence of the bio goes here. Second sentence goes here.",
    photo: "/images/cast/character-2.jpg",
    window: { left: 52.4, top: 35.4, width: 10.0, height: 12.0 }
  },
  {
    id: "character-3",
    name: "CHARACTER NAME",
    bio: "First sentence of the bio goes here. Second sentence goes here.",
    photo: "/images/cast/character-3.jpg",
    window: { left: 79.7, top: 35.4, width: 10.4, height: 12.0 }
  },
  {
    id: "character-4",
    name: "CHARACTER NAME",
    bio: "First sentence of the bio goes here. Second sentence goes here.",
    photo: "/images/cast/character-4.jpg",
    window: { left: 23.1, top: 51.5, width: 12.7, height: 11.9 }
  },
  {
    id: "character-5",
    name: "Candy",
    bio: "Candy Balcamino, AUD, is the building's resident audiologist, and she has heard things she was never supposed to hear. She'll adjust your hearing aid for free — the eavesdropping is extra.",
    photo: "/images/cast/character-5.png",
    window: { left: 65.8, top: 51.5, width: 9.8, height: 11.9 }
  },
  {
    id: "character-6",
    name: "CHARACTER NAME",
    bio: "First sentence of the bio goes here. Second sentence goes here.",
    photo: "/images/cast/character-6.jpg",
    window: { left: 51.6, top: 66.3, width: 10.9, height: 15.6 }
  }
];

const BUILDING_IMAGE = "/images/building.jpg";
const BG = "#0b0b0d";
const TEXT = "#ede6d8";
const ACCENT = "#e8a84c";

const GRAY_PLACEHOLDER =
  "data:image/svg+xml," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="600" height="800"><rect width="100%" height="100%" fill="#3a3a3e"/></svg>'
  );

export default function TheBuilding() {
  const [active, setActive] = useState(null);
  const [buildingError, setBuildingError] = useState(false);
  const debug = new URLSearchParams(window.location.search).get("debug") === "1";
  const closeRef = useRef(null);

  const close = useCallback(() => setActive(null), []);

  useEffect(() => {
    if (!active) return;
    const onKey = (e) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [active, close]);

  return (
    <div data-testid="the-building-page" style={{ background: BG, minHeight: "100vh", color: TEXT, position: "relative" }}>
      <SEO
        title="The Building"
        description="Everyone in this building is lying about something. Click a window. Meet the characters of DTLA Confidential, a darkish comedy by Laura Novak."
        ogImage="https://lauranovakwrites.com/images/building-share.jpg"
        url="https://lauranovakwrites.com/the-building"
      />
      <Helmet>
        <title>The Building | DTLA Confidential</title>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=Inter:wght@400;500&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <style>{`
        @keyframes tb-card-in {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes tb-flicker {
          0%, 100% { opacity: 0; }
          8% { opacity: 0.10; }
          14% { opacity: 0.02; }
          22% { opacity: 0.12; }
          36% { opacity: 0.04; }
          52% { opacity: 0.14; }
          68% { opacity: 0.03; }
          82% { opacity: 0.09; }
        }
        .tb-flicker {
          position: absolute;
          pointer-events: none;
          background: radial-gradient(ellipse at center, rgba(232, 168, 76, 0.9) 0%, rgba(232, 168, 76, 0.3) 70%, transparent 100%);
          mix-blend-mode: screen;
          animation: tb-flicker 7s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .tb-flicker { animation: none; opacity: 0; }
        }
        .tb-hotspot { background: transparent; border: none; padding: 0; cursor: pointer; transition: background-color 0.2s ease; }
        @media (hover: hover) {
          .tb-hotspot:hover { background-color: rgba(232, 168, 76, 0.28); }
        }
        .tb-hotspot:focus-visible { outline: 2px solid ${ACCENT}; outline-offset: 2px; }
      `}</style>

      <Link
        to="/"
        data-testid="building-back-home-link"
        style={{
          position: "absolute",
          top: "20px",
          left: "24px",
          fontFamily: "'Inter', sans-serif",
          fontVariant: "small-caps",
          letterSpacing: "0.14em",
          fontSize: "13px",
          color: TEXT,
          textDecoration: "none",
          opacity: 0.7,
          transition: "opacity 0.2s ease, color 0.2s ease",
          zIndex: 10
        }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; e.currentTarget.style.color = ACCENT; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = 0.7; e.currentTarget.style.color = TEXT; }}
      >
        &larr; home
      </Link>

      <p
        data-testid="building-tagline"
        style={{
          fontFamily: "'Inter', sans-serif",
          fontVariant: "small-caps",
          letterSpacing: "0.18em",
          fontSize: "13px",
          textAlign: "center",
          padding: "28px 16px 20px"
        }}
      >
        everyone in this building is lying about something. click a window.
      </p>

      <div style={{ position: "relative", width: "100%" }}>
        {buildingError ? (
          <div style={{ width: "100%", paddingTop: "66.67%", background: "#3a3a3e" }} />
        ) : (
          <img
            src={BUILDING_IMAGE}
            alt="A downtown Los Angeles loft building at dusk with lit windows"
            style={{ display: "block", width: "100%", height: "auto" }}
            onError={() => setBuildingError(true)}
          />
        )}

        {characters.map((c, i) => (
          <div
            key={`glow-${c.id}`}
            className="tb-flicker"
            style={{
              left: `${c.window.left}%`,
              top: `${c.window.top}%`,
              width: `${c.window.width}%`,
              height: `${c.window.height}%`,
              animationDelay: `${(i * 1.7) % 7}s`,
              animationDuration: `${6 + (i % 3) * 2}s`
            }}
          />
        ))}

        {characters.map((c) => (
          <button
            key={c.id}
            data-testid={`hotspot-${c.id}`}
            className="tb-hotspot"
            aria-label={c.name}
            onClick={() => setActive(c)}
            style={{
              position: "absolute",
              left: `${c.window.left}%`,
              top: `${c.window.top}%`,
              width: `${c.window.width}%`,
              height: `${c.window.height}%`,
              ...(debug ? { outline: `1px solid ${ACCENT}` } : {})
            }}
          />
        ))}
      </div>

      {active && (
        <div
          data-testid="character-lightbox"
          onClick={close}
          role="dialog"
          aria-modal="true"
          aria-label={active.name}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
            padding: "0 16px"
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              background: BG,
              color: TEXT,
              borderRadius: "8px",
              boxShadow: "0 24px 64px rgba(0, 0, 0, 0.6)",
              maxWidth: "480px",
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
              padding: "20px 20px 28px",
              animation: "tb-card-in 200ms ease-out"
            }}
          >
            <button
              ref={closeRef}
              data-testid="lightbox-close-btn"
              onClick={close}
              aria-label="Close"
              style={{
                position: "absolute",
                top: "10px",
                right: "12px",
                background: "transparent",
                border: "none",
                color: ACCENT,
                fontSize: "26px",
                lineHeight: 1,
                cursor: "pointer",
                padding: "6px"
              }}
            >
              &times;
            </button>

            <img
              src={active.photo}
              alt={active.name}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = GRAY_PLACEHOLDER;
              }}
              style={{
                display: "block",
                width: "100%",
                maxHeight: "60vh",
                objectFit: "cover",
                borderRadius: "6px"
              }}
            />

            <h2
              data-testid="lightbox-character-name"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontWeight: 700,
                fontSize: "32px",
                margin: "18px 0 10px"
              }}
            >
              {active.name}
            </h2>

            <p
              data-testid="lightbox-character-bio"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "18px",
                lineHeight: 1.6,
                margin: 0
              }}
            >
              {active.bio}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
